import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import { Link } from 'react-router-dom';
import TaskSkeleton from './TaskSkeleton';

export default function StreamCategoryList() {
    const { http, user } = AuthUser();
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        desc: '',
        language: '',
        etc_1: '',
        etc_2: '',
        etc_3: '',
        thumb: null
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = () => {
        setIsLoading(true);
        http.get('/stream_categories').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                setCategories(res.data.data);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch categories");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching categories:", error);
            toast.error("Error fetching categories");
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            thumb: e.target.files[0]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('desc', formData.desc);
        submitData.append('language', formData.language);
        submitData.append('etc_1', formData.etc_1);
        submitData.append('etc_2', formData.etc_2);
        submitData.append('etc_3', formData.etc_3);
        
        if (formData.thumb) {
            submitData.append('thumb', formData.thumb);
        }

        try {
            let response;
            if (editingCategory) {
                // Use POST with _method for updates (Laravel method spoofing)
                submitData.append('_method', 'PUT');
                response = await http.post(`/stream_categories/${editingCategory.id}`, submitData);
                toast.success("Category updated successfully!");
            } else {
                response = await http.post('/stream_categories', submitData);
                toast.success("Category created successfully!");
            }

            if (response.data.success) {
                setShowModal(false);
                setFormData({
                    name: '',
                    desc: '',
                    language: '',
                    etc_1: '',
                    etc_2: '',
                    etc_3: '',
                    thumb: null
                });
                setEditingCategory(null);
                fetchCategories();
            }
        } catch (error) {
            console.error("Error saving category:", error);
            toast.error("Error saving category");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            desc: category.desc,
            language: category.language || '',
            etc_1: category.etc_1 || '',
            etc_2: category.etc_2 || '',
            etc_3: category.etc_3 || '',
            thumb: null
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this category?")) {
            try {
                const response = await http.delete(`/stream_categories/${id}`);
                if (response.data.success) {
                    toast.success("Category deleted successfully!");
                    fetchCategories();
                }
            } catch (error) {
                console.error("Error deleting category:", error);
                toast.error("Error deleting category");
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            desc: '',
            language: '',
            etc_1: '',
            etc_2: '',
            etc_3: '',
            thumb: null
        });
        setEditingCategory(null);
        setShowModal(false);
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title">Stream Categories</h3>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowModal(true)}
                            >
                                + Add Category
                            </button>
                        </div>
                        <div className="card-body">
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Language</th>
                                                <th>Thumbnail</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {categories.map((category) => (
                                                <tr key={category.id}>
                                                    <td>{category.id}</td>
                                                    <td>{category.name}</td>
                                                    <td>{category.desc}</td>
                                                    <td>{category.language || '-'}</td>
                                                    <td>
                                                        {category.thumb ? (
                                                            <img 
                                                                src={`http://3.6.125.26/api/api/stream_categories/${category.id}/image`}
                                                                alt="Thumbnail"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                className="rounded"
                                                            />
                                                        ) : (
                                                            <span className="text-muted">No Image</span>
                                                        )}
                                                    </td>
                                                    <td>{moment(category.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                    <td>
                                                        <button 
                                                            className="btn btn-sm btn-warning me-2"
                                                            onClick={() => handleEdit(category)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(category.id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={resetForm}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Description *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="desc"
                                                    value={formData.desc}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">Language *</label>
                                                <select
                                                    className="form-control"
                                                    name="language"
                                                    value={formData.language}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Language</option>
                                                    <option value="English">English</option>
                                                    <option value="Hindi">Hindi</option>
                                                    <option value="Kannada">Kannada</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label">ETC 1</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="etc_1"
                                                    value={formData.etc_1}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label">ETC 2</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="etc_2"
                                                    value={formData.etc_2}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label">ETC 3</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="etc_3"
                                                    value={formData.etc_3}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Thumbnail Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                        <small className="form-text text-muted">
                                            Supported formats: JPG, PNG, GIF (Max 5MB)
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
